import React, { Component, Fragment } from 'react'
import { get, includes, isEmpty, join, map, remove, size } from 'lodash'
import { produce } from 'immer'
import Actions, { ActionName } from './Actions'
import DateTooltip from './DateTooltip'
import TextTooltip from './TextTooltip'
import Label, { LabelType } from './Label'
import DownloadLink from './DownloadLink'
import Checkbox from './Checkbox'
import csvUtil from '../utils/csv.util'

export interface TableHorizontalHeader {
    heading: string
    path?: string | string[]
    date?: boolean
    downloadText?: string
    breakable?: boolean
    truncate?: true
    editable?: boolean
    image?: boolean
    tooltip?: string[]
    badge?: string
    delta?: boolean
    isBadge?: boolean
}

interface TableHorizontalDataBase {
    id: number
    actions?: ActionName[]
    badge?: LabelType
}

export type TableHorizontalData<Data = {}> = Data &
    TableHorizontalDataBase &
    Record<string, any>

export interface TableHorizontalProps<Data = {}> {
    headers: TableHorizontalHeader[]
    data: TableHorizontalData<Data>[]
    selectedIds?: number[]
    onActionClick?: (id: number, action: ActionName, additionalInfo?: string) => void
    onSelect?: (ids: number[]) => void
    onEditableChange?: (
        id: number,
        path: TableHorizontalHeader['path'],
        value: string
    ) => void
}

export default class TableHorizontal extends Component<TableHorizontalProps> {
  get headers () {
    return this.props.headers.map(header => (
            <th
                className="table-head"
                key={header.heading}>
                {header.heading}
            </th>
    ))
  }

  resolvePathValue (data: TableHorizontalData, path: string | string[] | undefined) {
    if (!path) return null
    if (Array.isArray(path)) {
      for (const p of path) {
        const value = get(data, p)
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
      return null
    } else {
      return get(data, path)
    }
  }

  tooltipText (data: TableHorizontalData, tooltip: string[] | undefined) {
    const tooltipValues = map(tooltip, tooltip => {
      if (tooltip === '-') return tooltip
      return get(data, tooltip)
    })
    return join(tooltipValues, ' ')
  }

  actions (id: number, actions: ActionName[], additionalInfo?: string) {
    const { onActionClick } = this.props
    return onActionClick &&
            <Actions
                id={id}
                actions={actions}
                additionalInfo={additionalInfo}
                onClick={onActionClick}/>
  }

  date (value: string, tooltipText: string | null) {
    return (
            <DateTooltip
                date={value}
                tooltip={tooltipText}/>
    )
  }

  textTooltip (text: string, tooltipText: string | null, truncate: true | undefined) {
    return (
            <TextTooltip
                text={text}
                tooltip={tooltipText}
                truncate={truncate}/>
    )
  }

  editable (id: number, path: TableHorizontalHeader['path'], value: string) {
    const { onEditableChange } = this.props
    const handleEditableChange = () => {
      return onEditableChange && onEditableChange(id, path, value)
    }
    return onEditableChange &&
            <input
                defaultValue={value}
                className="border border-primary outline-none"
                onChange={handleEditableChange}/>
  }

  image (url: string) {
    return (
            <img
                src={url}
                className="h-6"/>
    )
  }

  badgeValue (header: TableHorizontalHeader, data: TableHorizontalData) {
    const { badge } = header
    if (!badge) return null
    return get(data, badge)
  }

  dataValue (header: TableHorizontalHeader, data: TableHorizontalData) {
    const { date, path, image, editable, tooltip, downloadText, truncate } = header
    const { id, actions, additionalInfo } = data
    const tooltipText = this.tooltipText(data, tooltip)
    if (path) {
      const value = this.resolvePathValue(data, path)
      if (editable) {
        return this.editable(id, path, value)
      } else if (image) {
        return this.image(value)
      } else if (date) {
        return value && this.date(value, tooltipText)
      } else if (downloadText) {
        const text = get(data, downloadText)
        const sanitizedText = csvUtil.sanitizeFilename(text)
        return <DownloadLink path={value} text={sanitizedText}/>
      } else if (tooltipText) {
        return this.textTooltip(value, tooltipText, truncate)
      } else return value
    } else if (actions) {
      return this.actions(id, actions, additionalInfo)
    } else return null
  }

  badge (header: TableHorizontalHeader, data: TableHorizontalData) {
    const text = this.badgeValue(header, data)
    return text &&
            <Label type='light' size='regular' text={text} className={'ml-1'}/>
  }

  tdData (value: any, header: TableHorizontalHeader, data: TableHorizontalData) {
    if (data.badge && header.isBadge) {
      const text = this.dataValue(header, data)
      return text &&
                <Label type={data.badge} size='regular' text={text} className={'ml-1'}/>
    } else return value
  }

  data (key: string, value: any, header: TableHorizontalHeader, data: TableHorizontalData) {
    const baseClass = 'table-data'
    const breakableClass = header.breakable && 'whitespace-normal'
    const truncateClass = header.truncate && 'max-w-xs'
    const deltaClass = header.delta && (value >= 0 ? 'font-medium text-green-500' : 'font-medium text-red-500')
    const className = `${baseClass} ${breakableClass} ${truncateClass} ${deltaClass}`
    const wrapperClass = header.truncate ? 'block' : 'flex items-center'
    return (
            <td
                className={className}
                key={key}>
                <div className={wrapperClass}>
                    {this.tdData(value, header, data)}
                    {this.badge(header, data)}
                </div>
            </td>
    )
  }

  isSelected (id: number) {
    const { selectedIds } = this.props
    if (!selectedIds) return false
    return includes(selectedIds, id)
  }

  get isSelectedAll () {
    const { selectedIds, data } = this.props
    if (!selectedIds) return false
    return selectedIds.length === data.length
  }

  handleSelectedChange = (name: string) => {
    const { onSelect, selectedIds: oldSelectedIds } = this.props
    if (!onSelect) return
    if (!oldSelectedIds) return
    const id = Number(name)
    const selectedIds = produce(oldSelectedIds, selectedIds => {
      const isExists = includes(selectedIds, id)
      if (isExists) {
        remove(selectedIds, selectedId => selectedId === id)
      } else {
        selectedIds.push(id)
      }
      return selectedIds
    })
    onSelect(selectedIds)
  }

  handleSelectAllChange = () => {
    const { onSelect } = this.props
    if (!onSelect) return
    let selectedIds: number[] = []
    if (!this.isSelectedAll) {
      selectedIds = map(this.props.data, data => data.id)
    }
    onSelect(selectedIds)
  }

  select (data: TableHorizontalData) {
    const { onSelect } = this.props
    return onSelect && (
            <td className='table-data'>
                <Checkbox
                    name={String(data.id)}
                    checked={this.isSelected(data.id)}
                    onChange={this.handleSelectedChange}
                    className="mb-0"/>
            </td>
    )
  }

  dataRow (data: TableHorizontalData) {
    return this.props.headers.map(header => {
      const key = `${data.id}-${header.heading}`
      const dataValue = this.dataValue(header, data)
      return (
                <Fragment key={key}>
                    {this.data(key, dataValue, header, data)}
                </Fragment>
      )
    })
  }

  get selectAllCheckbox () {
    const { onSelect, data } = this.props
    const hasData = data.length !== 0
    const canShow = Boolean(onSelect) && hasData
    return canShow && (
            <th className="table-head">
                <Checkbox
                    name="selectAll"
                    checked={this.isSelectedAll}
                    onChange={this.handleSelectAllChange}
                    className="mb-0"/>
            </th>
    )
  }

  get dataRows () {
    return map(this.props.data, data => (
            <tr key={data.id}>
                {this.select(data)}
                {this.dataRow(data)}
            </tr>
    ))
  }

  get noResults () {
    const colspan = size(this.props.headers)
    return isEmpty(this.props.data) &&
            <tr>
                <td
                    className="table-data text-center"
                    colSpan={colspan}>
                    No results found
                </td>
            </tr>
  }

  render () {
    return (
            <div className="table-responsive">
                <table className="table-default">
                    <thead>
                    <tr>
                        {this.selectAllCheckbox}
                        {this.headers}
                    </tr>
                    </thead>
                    <tbody>
                    {this.dataRows}
                    {this.noResults}
                    </tbody>
                </table>
            </div>
    )
  }
}
